import React, { useEffect, useState } from "react";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";

/* The end of the run: what was produced, and how to take it away.
 *
 * Twenty minutes of work previously ended on a single grey sentence, which is
 * an odd way to finish something this long. The last card should hand over the
 * artefact - the same way a person finishing a piece of work gives you the file
 * rather than telling you it exists.
 *
 * WHY A BLOB FETCH AND NOT A LINK. The download endpoint is authenticated, and
 * a plain <a href> carries no Authorization header - it would answer 401 or
 * bounce to a login page. So the file is fetched through the same client every
 * other call uses, then handed to the browser as an object URL.
 */

const FILES = [
  {
    type: "pdf",
    label: "Proposal",
    kind: "PDF document",
    icon: (
      <>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <polyline points="14 3 14 8 19 8" />
        <path d="M9 13h6M9 16.5h4" />
      </>
    ),
  },
  {
    type: "docx",
    label: "Proposal",
    kind: "Word document",
    icon: (
      <>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <polyline points="14 3 14 8 19 8" />
        <path d="m9 12 1.5 5 1.5-3.5 1.5 3.5L15 12" />
      </>
    ),
  },
];

const ProposalCompleteCard = ({ message, onOpenDoc, docOpen }) => {
  const { projectId, projectName, templateId, partial, sectionsWritten, sectionsTotal,
    oneGo } = message || {};
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/v1/rfp/folders/${projectId}/proposal/outline/confirm/`);
        const toc = res?.data?.toc || [];
        if (!alive) return;
        const nodes = toc.flatMap((s) => [s, ...(s.children || [])]);
        setStats({
          sections: toc.length,
          words: nodes.reduce((a, n) => a + (n.words || 0), 0),
          sources: nodes.reduce((a, n) => a + (n.sources || 0), 0),
        });
      } catch {
        /* the card is still useful without the numbers */
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId]);

  // One place decides the filename, so the label on the row and the file that
  // lands on disk cannot disagree.
  const safeName = (projectName || "Proposal").replace(/[^\w.-]+/g, "_");

  const download = async (type) => {
    setBusy(type);
    setError(null);
    try {
      // Omitted rather than sent empty when no design was picked: the server
      // falls through folder pin -> workspace default -> built-in, and a blank
      // template_id would skip that chain rather than start it.
      const design = templateId ? `&template_id=${encodeURIComponent(templateId)}` : "";
      const res = await api.get(
        `/api/v1/rfp/folders/${projectId}/generate-proposal/download/?type=${type}${design}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      // NAME IT WHAT IT ACTUALLY IS. A one-go proposal exports as DOCX whatever
      // `type` asked for, and completion cards saved before the card knew which
      // path wrote them still show a PDF button. Trusting the request over the
      // response there writes Word bytes into a .pdf nothing can open.
      const mime = res.data?.type || "";
      const ext = mime.includes("wordprocessingml")
        ? "docx"
        : mime.includes("pdf")
          ? "pdf"
          : type;
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick, not immediately: revoking synchronously can
      // cancel the download the click just started.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError(`Couldn't produce the ${type.toUpperCase()}.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Shell
      width={640}
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      }
      /* An export asked for mid-run is not a finished proposal. Calling it one
         put "Proposal complete" above a thread that was still writing section
         7, and the file it hands over is only as long as what exists. */
      title={partial ? "Export what is written so far" : "Proposal complete"}
      meta={
        partial ? (
          <span className="text-[12px] tabular-nums text-[#92620B]">
            {sectionsWritten} of {sectionsTotal} sections written
          </span>
        ) : stats ? (
          <span className="text-[12px] tabular-nums text-[#6B6472]">
            {stats.sections} sections · {stats.words.toLocaleString()} words
          </span>
        ) : null
      }
      footer={
        <>
          <a
            href={`/rfp-projects/project/${projectId}/proposal`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#18101C]/[0.12] bg-white px-3 py-2 text-[13px] font-medium text-[#3F3F46] transition-colors duration-150 hover:border-[#18101C]/25 hover:bg-[#18101C]/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
          >
            Open in the editor
          </a>
          {!docOpen && onOpenDoc && (
            <button
              type="button"
              onClick={onOpenDoc}
              className="ml-auto text-[12.5px] font-medium text-[#6B6472] underline decoration-[#6B6472]/25 underline-offset-[3px] transition-colors hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
            >
              Read it here
            </button>
          )}
        </>
      }
    >
      <div className="space-y-2 px-4 py-3.5">
        {/* A one-go proposal exports as DOCX only.
          *
          * It is one body of HTML written in a single pass, not a section
          * tree, so the templated PDF renderer has nothing to walk. The DOCX
          * is built plain — no export template — because that template's
          * cover and header fields are user-editable free text and were
          * stamping placeholder junk on every page of a real export. */}
        {(oneGo ? FILES.filter((f) => f.type === "docx") : FILES).map((f) => (
          <div
            key={f.type}
            className="flex items-center gap-3 rounded-xl border border-[#18101C]/[0.09] bg-white px-3 py-2.5"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#A04A97]/[0.09] text-[#A04A97]">
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                {f.icon}
              </svg>
            </span>

            <span className="min-w-0 flex-1">
              {/* The FILENAME is the title, not the project name. Both rows
                  said "INS" with only a subtitle to tell them apart, so they
                  read as the same file listed twice. The extension is what the
                  reader is choosing between, and it is what lands on disk. */}
              <span className="block truncate text-[13.5px] font-semibold text-[#18101C]">
                {safeName}.{f.type}
              </span>
              <span className="block text-[11.5px] text-[#77707E]">{f.kind}</span>
            </span>

            <button
              type="button"
              onClick={() => download(f.type)}
              disabled={!!busy}
              className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-[#18101C]/[0.12] bg-white px-2.5 py-1.5 text-[12.5px] font-medium text-[#3F3F46] transition-colors duration-150 hover:border-[#18101C]/25 hover:bg-[#18101C]/[0.03] disabled:cursor-not-allowed disabled:opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
            >
              {busy === f.type ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#A04A97]/30 border-t-[#A04A97] motion-reduce:animate-none" />
              ) : (
                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v12" />
                  <polyline points="7 11 12 16 17 11" />
                  <path d="M4 20h16" />
                </svg>
              )}
              {busy === f.type ? "Preparing…" : "Download"}
            </button>
          </div>
        ))}

        {error && (
          <p role="alert" className="pt-1 text-[12.5px] text-[#B91C1C]">
            {error}
          </p>
        )}
      </div>
    </Shell>
  );
};

export default ProposalCompleteCard;
